import { useCallback, useMemo, useReducer } from 'react';
import {
  type ComposeFormState,
  INITIAL_COMPOSE_STATE,
  type SendMode,
} from './types';
import type {
  ImageCrop,
  NotificationAudience,
  NotificationTargetType,
} from '@/queries/notifications';

type Action =
  | { type: 'set'; key: keyof ComposeFormState; value: ComposeFormState[keyof ComposeFormState] }
  | { type: 'reset' }
  | { type: 'load'; partial: Partial<ComposeFormState> };

function reducer(state: ComposeFormState, action: Action): ComposeFormState {
  switch (action.type) {
    case 'set':
      return { ...state, [action.key]: action.value };
    case 'reset':
      return INITIAL_COMPOSE_STATE;
    case 'load':
      return { ...state, ...action.partial };
    default:
      return state;
  }
}

export interface FormControls {
  state: ComposeFormState;
  set: <K extends keyof ComposeFormState>(key: K, value: ComposeFormState[K]) => void;
  load: (partial: Partial<ComposeFormState>) => void;
  reset: () => void;
  isDirty: boolean;
  canSend: boolean;
  audience: Partial<NotificationAudience>;
  effectiveImageUrl: string | undefined;
  buildBasePayload: () => {
    title: string;
    body: string;
    image_url?: string;
    image_crop: ImageCrop;
    image_bg_color?: string;
    target?: { type: NotificationTargetType; value: string };
    audience: Partial<NotificationAudience>;
  };
}

export function useNotificationFormState(): FormControls {
  const [state, dispatch] = useReducer(reducer, INITIAL_COMPOSE_STATE);

  const set = useCallback(
    <K extends keyof ComposeFormState>(key: K, value: ComposeFormState[K]) => {
      dispatch({ type: 'set', key, value });
    },
    [],
  );

  const load = useCallback((partial: Partial<ComposeFormState>) => {
    dispatch({ type: 'load', partial });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: 'reset' });
  }, []);

  const isDirty = useMemo(() => {
    return (
      state.title !== INITIAL_COMPOSE_STATE.title ||
      state.body !== INITIAL_COMPOSE_STATE.body ||
      state.imageUrl !== INITIAL_COMPOSE_STATE.imageUrl ||
      state.imageCrop !== INITIAL_COMPOSE_STATE.imageCrop ||
      state.imageBgEnabled !== INITIAL_COMPOSE_STATE.imageBgEnabled ||
      state.targetType !== INITIAL_COMPOSE_STATE.targetType ||
      state.targetValue !== INITIAL_COMPOSE_STATE.targetValue ||
      state.sendMode !== INITIAL_COMPOSE_STATE.sendMode ||
      state.abEnabled !== INITIAL_COMPOSE_STATE.abEnabled ||
      state.titleB !== INITIAL_COMPOSE_STATE.titleB ||
      state.bodyB !== INITIAL_COMPOSE_STATE.bodyB
    );
  }, [state]);

  const canSend = useMemo(
    () => Boolean(state.title.trim() && state.body.trim()),
    [state.title, state.body],
  );

  const audience: Partial<NotificationAudience> = useMemo(() => {
    const platforms: NotificationAudience['platforms'] = [];
    if (state.includeIos) platforms.push('ios');
    if (state.includeAndroid) platforms.push('android');
    const locales: string[] = [];
    if (state.includeAr) locales.push('ar');
    if (state.includeEn) locales.push('en');
    return { platforms, locales, login_status: state.loginStatus };
  }, [
    state.includeIos,
    state.includeAndroid,
    state.includeAr,
    state.includeEn,
    state.loginStatus,
  ]);

  const effectiveImageUrl = useMemo(() => {
    const explicit = state.imageUrl.trim();
    if (explicit) return explicit;
    if (
      (state.targetType === 'product' || state.targetType === 'collection') &&
      state.targetThumbnail
    ) {
      return state.targetThumbnail;
    }
    return undefined;
  }, [state.imageUrl, state.targetType, state.targetThumbnail]);

  const buildBasePayload = useCallback(() => {
    return {
      title: state.title.trim(),
      body: state.body.trim(),
      image_url: effectiveImageUrl,
      image_crop: state.imageCrop,
      image_bg_color: state.imageBgEnabled ? state.imageBgColor : undefined,
      target:
        state.targetType === 'none'
          ? undefined
          : { type: state.targetType, value: state.targetValue.trim() },
      audience,
    };
  }, [
    state.title,
    state.body,
    state.imageCrop,
    state.imageBgEnabled,
    state.imageBgColor,
    state.targetType,
    state.targetValue,
    effectiveImageUrl,
    audience,
  ]);

  return {
    state,
    set,
    load,
    reset,
    isDirty,
    canSend,
    audience,
    effectiveImageUrl,
    buildBasePayload,
  };
}

export function getPrimaryActionLabel(state: ComposeFormState): string {
  if (state.abEnabled) return 'ابعت A/B Test';
  if (state.sendMode === 'now') return 'ابعت لكل الجمهور';
  if (state.sendMode === 'one_time') {
    return state.scheduleAt ? `اجدول لـ ${formatScheduleAt(state.scheduleAt)}` : 'اجدول';
  }
  if (state.sendMode === 'recurring') return 'فعّل Schedule دوري';
  if (state.sendMode === 'trigger') return 'فعّل Trigger';
  return 'ابعت';
}

function formatScheduleAt(dt: string): string {
  try {
    const d = new Date(dt);
    return d.toLocaleString('ar-EG', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dt;
  }
}

export function buildLinkPreview(
  type: SendMode | 'product' | 'collection' | 'order' | 'url' | 'none',
  value: string,
): string {
  const v = value.trim();
  if (type === 'none' || !v) return '';
  if (type === 'product') return `padelneeds://product/${v}`;
  if (type === 'collection') return `padelneeds://collection/${v}`;
  if (type === 'order') return `padelneeds://order/${v}`;
  if (type === 'url') return v;
  return '';
}

export function formatTime(epoch: number | null | undefined): string {
  if (!epoch) return '—';
  try {
    return new Date(epoch * 1000).toLocaleString();
  } catch {
    return '—';
  }
}
